import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { useState, useMemo } from 'react'
import {
  Search, RefreshCw, Package, XCircle, Loader2,
  ChevronDown, Trash2, Plus, Filter, X, RotateCcw, Link2, MapPin, ClipboardCopy, MessageSquareText
} from 'lucide-react'
import { shipping, ShippingOrder, ManualOrder, GroupedOrderInfo, prefactura } from '@/lib/api'
import { useToast } from '@/components/ui/Toast'
import { useAuth } from '@/contexts/AuthContext'
import { CombinedFlag, FlagTooltip, InvoiceCheckModal, MessagesReaderModal, ReaderItem, combineStatus, otherReasons, unreadMessages } from './InvoiceCheck'
import { CountryFlag, CarrierBadge, PaymentBadge, ViesBadge } from '@/components/CarrierLogos'
import OrderDetailModal from './OrderDetailModal'
import ManualOrderModal from './ManualOrderModal'

// Países soportados por los transportistas (UE + UK + Suiza + Noruega)
const SUPPORTED_COUNTRIES = [
  'ES', 'PT', 'FR', 'DE', 'IT', 'BE', 'NL', 'AT', 'PL', 'GB', 'IE',
  'FI', 'SE', 'DK', 'NO', 'CH',  // Nórdicos + Suiza
  'CZ', 'HU', 'SK', 'SI', 'HR', 'RO', 'BG',  // Europa del Este
  'LT', 'LV', 'EE',  // Bálticos
  'LU', 'GR', 'CY', 'MT'  // Otros UE
]

// Validación de pedidos - retorna estado del semáforo
type ValidationStatus = 'green' | 'yellow' | 'red'
interface ValidationResult {
  status: ValidationStatus
  issues: string[]
}

function validateOrder(order: ShippingOrder): ValidationResult {
  const issues: string[] = []
  let hasError = false
  let hasWarning = false

  const addr = order.shipping_address

  // 🔴 CRÍTICOS - Bloquean envío

  // Código postal inválido
  if (!addr.postal_code || addr.postal_code.length < 4) {
    issues.push('Código postal inválido')
    hasError = true
  }

  // País no soportado
  if (!SUPPORTED_COUNTRIES.includes(addr.country_code)) {
    issues.push(`País no soportado: ${addr.country_code}`)
    hasError = true
  }

  // 🟡 ADVERTENCIAS

  // Sin teléfono móvil
  const phone = addr.phone_mobile || addr.phone || ''
  if (!phone || phone.length < 9) {
    issues.push('Sin teléfono válido')
    hasWarning = true
  }

  // Sin email
  if (!order.customer_email || !order.customer_email.includes('@')) {
    issues.push('Sin email válido')
    hasWarning = true
  }

  // Dirección muy corta
  if (!addr.street || addr.street.length < 5) {
    issues.push('Dirección incompleta')
    hasWarning = true
  }

  // (Quitado 7-oct-2026: "Envío internacional - verificar descripción" ya no es motivo de
  // aviso; la descripción para aduanas la resuelve el gestor de etiquetas de FedEx.)

  if (hasError) {
    return { status: 'red', issues }
  } else if (hasWarning) {
    return { status: 'yellow', issues }
  }
  return { status: 'green', issues: [] }
}

// Componente indicador de estado - estilo banderita como Outvio
function StatusIndicator({ order }: { order: ShippingOrder }) {
  const validation = validateOrder(order)

  const config = {
    green: {
      color: 'text-green-500',
      bgColor: 'bg-green-500',
    },
    yellow: {
      color: 'text-amber-500',
      bgColor: 'bg-amber-500',
    },
    red: {
      color: 'text-red-500',
      bgColor: 'bg-red-500',
    }
  }

  const { bgColor } = config[validation.status]

  // Tooltip en portal (FlagTooltip): con pocos pedidos la tabla recortaba el tooltip absoluto
  const tooltip = validation.issues.length > 0 ? (
    <div className="bg-slate-900 text-white text-xs rounded-lg p-2 shadow-lg min-w-48 whitespace-nowrap">
      <p className="font-medium mb-1">Problemas detectados:</p>
      <ul className="space-y-0.5">
        {validation.issues.map((issue, i) => (
          <li key={i} className="flex items-center gap-1">
            <span className="w-1 h-1 bg-current rounded-full" />
            {issue}
          </li>
        ))}
      </ul>
    </div>
  ) : (
    <div className="bg-slate-900 text-white text-xs rounded-lg px-2 py-1 shadow-lg whitespace-nowrap">
      Datos correctos
    </div>
  )

  return (
    <FlagTooltip className="relative flex items-center justify-center" content={tooltip}>
      {/* Banderita de color */}
      <div className={`w-4 h-5 ${bgColor} rounded-sm relative`}>
        {/* Corte triangular en la parte inferior para simular bandera */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[8px] border-r-[8px] border-b-[4px] border-l-transparent border-r-transparent border-b-white dark:border-b-slate-800" />
      </div>
    </FlagTooltip>
  )
}

// Logo de PrestaShop como fuente
function PrestaShopLogo({ className = "w-8 h-8" }: { className?: string }) {
  return (
    <div className={`${className} rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center`}>
      <span className="text-white font-bold text-xs">PS</span>
    </div>
  )
}

// Logo de pedido manual
function ManualOrderLogo({ className = "w-8 h-8" }: { className?: string }) {
  return (
    <div className={`${className} rounded-full bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center`}>
      <span className="text-white font-bold text-xs">M</span>
    </div>
  )
}

// Tipo unificado para mostrar en la tabla
interface UnifiedOrder {
  id: number
  reference: string
  date_add: string
  customer_name: string
  customer_email: string
  shipping_address: {
    country_code: string
    postal_code: string
    street: string
    city: string
    phone?: string
    phone_mobile?: string
  }
  total: number
  items_count: number
  carrier_name: string
  payment_method: string
  dni?: string
  vat_number?: string
  has_tax: boolean  // true si el pedido tiene IVA cobrado
  source: 'prestashop' | 'manual'
  original: ShippingOrder | ManualOrder
  // Información de agrupación (envío gratuito por pedido anterior)
  is_grouped?: boolean
  grouped_with?: GroupedOrderInfo
  service_point?: ShippingOrder['service_point']
}

// Interfaz de filtros
interface OrderFilters {
  carrier: string[]
  payment: string[]
  country: string[]
  status: ValidationStatus[]
  viesOnly: boolean
}

const HIDE_NO_STOCK_KEY = 'shipping.orders.hideNoStock'
const STATE_NO_STOCK = 9  // Pedido pendiente por falta de stock (pagado) = pedido rosa

const isNoStockOrder = (o: { source: string; original: unknown }) =>
  o.source === 'prestashop' && (o.original as ShippingOrder).current_state === STATE_NO_STOCK

const emptyFilters: OrderFilters = {
  carrier: [],
  payment: [],
  country: [],
  status: [],
  viesOnly: false
}

// Componente de filtro dropdown
function FilterDropdown({
  label,
  options,
  selected,
  onChange,
  renderOption
}: {
  label: string
  options: string[]
  selected: string[]
  onChange: (values: string[]) => void
  renderOption?: (value: string) => React.ReactNode
}) {
  const [isOpen, setIsOpen] = useState(false)

  const toggleOption = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter(v => v !== value))
    } else {
      onChange([...selected, value])
    }
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors ${
          selected.length > 0
            ? 'bg-teal-100 text-teal-700 dark:bg-teal-900/50 dark:text-teal-300'
            : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600'
        }`}
      >
        <Filter className="w-3 h-3" />
        {selected.length > 0 && <span className="font-medium">{selected.length}</span>}
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute top-full left-0 mt-1 z-50 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 min-w-40 max-h-60 overflow-auto">
            <div className="p-2 border-b border-slate-100 dark:border-slate-700">
              <span className="text-xs font-medium text-slate-500 dark:text-slate-400">{label}</span>
            </div>
            <div className="p-1">
              {options.map(option => (
                <label
                  key={option}
                  className="flex items-center gap-2 px-2 py-1.5 hover:bg-slate-50 dark:hover:bg-slate-700/50 rounded cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(option)}
                    onChange={() => toggleOption(option)}
                    className="w-3.5 h-3.5 rounded border-slate-300 dark:border-slate-600 text-teal-600 focus:ring-teal-500"
                  />
                  <span className="text-sm text-slate-700 dark:text-slate-300">
                    {renderOption ? renderOption(option) : option}
                  </span>
                </label>
              ))}
            </div>
            {selected.length > 0 && (
              <div className="p-2 border-t border-slate-100 dark:border-slate-700">
                <button
                  onClick={() => { onChange([]); setIsOpen(false) }}
                  className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                >
                  Limpiar filtro
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}

// Helper para convertir códigos de país numéricos a ISO 2 letras
const COUNTRY_CODE_MAP: Record<string, string> = {
  '724': 'ES', '620': 'PT', '250': 'FR', '276': 'DE', '380': 'IT',
  '826': 'GB', '056': 'BE', '528': 'NL', '040': 'AT', '756': 'CH',
  '020': 'AD', '616': 'PL', '203': 'CZ', '348': 'HU', '372': 'IE',
  '208': 'DK', '752': 'SE', '246': 'FI', '578': 'NO', '300': 'GR',
}

function normalizeCountryCode(code: string | undefined): string {
  if (!code) return 'ES'
  const trimmed = code.trim()
  // Si ya es un código de 2 letras, devolverlo
  if (trimmed.length === 2 && /^[A-Za-z]+$/.test(trimmed)) {
    return trimmed.toUpperCase()
  }
  // Convertir código numérico
  return COUNTRY_CODE_MAP[trimmed] || COUNTRY_CODE_MAP[trimmed.padStart(3, '0')] || 'ES'
}

// Helper para detectar transportista
function getCarrierKey(carrierName: string | undefined): string {
  if (!carrierName) return 'Otro'
  const lower = carrierName.toLowerCase()
  if (lower.includes('mrw')) return 'MRW'
  if (lower.includes('gls')) return 'GLS'
  if (lower.includes('ctt')) return 'CTT'
  if (lower.includes('correos')) return 'Correos'
  if (lower.includes('seur')) return 'SEUR'
  return 'Otro'
}

// Helper para detectar forma de pago
function getPaymentKey(paymentMethod: string | undefined): string {
  if (!paymentMethod) return 'Otro'
  const lower = paymentMethod.toLowerCase()
  if (lower.includes('paypal')) return 'PayPal'
  if (lower.includes('tarjeta') || lower.includes('card') || lower.includes('stripe') || lower.includes('redsys')) return 'Tarjeta'
  if (lower.includes('transfer') || lower.includes('banco') || lower.includes('bank')) return 'Transferencia'
  if (lower.includes('reembolso') || lower.includes('cobran') || lower.includes('cod') || lower.includes('cash') || lower.includes('contra') || lower.includes('entrega')) return 'Contrareembolso'
  if (lower.includes('bizum')) return 'Bizum'
  return 'Otro'
}

interface OrdersTabProps {
  onNavigateToQueue?: () => void
}

export default function OrdersTab({ onNavigateToQueue }: OrdersTabProps) {
  const toast = useToast()
  const queryClient = useQueryClient()

  // Estado local
  const [selectedOrders, setSelectedOrders] = useState<number[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null)
  const [showManualOrderModal, setShowManualOrderModal] = useState(false)
  const [filters, setFilters] = useState<OrderFilters>(emptyFilters)
  // "Ocultar pedidos sin stock" (rosas, estado 9). Se recuerda en el navegador.
  const [hideNoStock, setHideNoStock] = useState<boolean>(() => {
    try { return localStorage.getItem(HIDE_NO_STOCK_KEY) === '1' } catch { return false }
  })
  const toggleHideNoStock = (value: boolean) => {
    setHideNoStock(value)
    try { localStorage.setItem(HIDE_NO_STOCK_KEY, value ? '1' : '0') } catch { /* sin almacenamiento */ }
  }
  // Semáforo pre-factura (se fusiona con la bandera de envío). Sin permiso, la bandera es la de siempre.
  const { hasPermission } = useAuth()
  const canInvoice = hasPermission('logistics.prefactura')
  const [invoiceModalId, setInvoiceModalId] = useState<number | null>(null)
  const [readerItems, setReaderItems] = useState<ReaderItem[] | null>(null)

  // Query para obtener pedidos pendientes de PrestaShop
  const ordersQuery = useQuery({
    queryKey: ['shipping-orders-pending'],
    queryFn: () => shipping.listOrders({
      status: undefined,
      include_shipped: false,
      limit: 500
    }),
  })

  // Query para obtener pedidos manuales
  const manualOrdersQuery = useQuery({
    queryKey: ['manual-orders-pending'],
    queryFn: () => shipping.listManualOrders({ status: 'pending' }),
  })

  // Query para obtener IDs de pedidos ya en cola (para excluirlos)
  const queueIdsQuery = useQuery({
    queryKey: ['shipping-queue-ids'],
    queryFn: () => shipping.getQueueOrderIds(),
  })

  // Mutation para añadir a cola de envío
  const addToQueueMutation = useMutation({
    mutationFn: async (orderIds: number[]) => {
      // Separar IDs según el tipo de pedido (PrestaShop vs Manual)
      const psOrderIds: number[] = []
      const manualOrderIds: number[] = []

      orderIds.forEach(id => {
        const order = pendingOrders.find(o => o.id === id)
        if (order?.source === 'manual') {
          manualOrderIds.push(id)
        } else {
          psOrderIds.push(id)
        }
      })

      return shipping.addToQueue({
        order_ids: psOrderIds,
        manual_order_ids: manualOrderIds
      })
    },
    onSuccess: (data) => {
      toast.success(data.message)
      setSelectedOrders([])
      queryClient.invalidateQueries({ queryKey: ['shipping-orders-pending'] })
      queryClient.invalidateQueries({ queryKey: ['manual-orders-pending'] })
      queryClient.invalidateQueries({ queryKey: ['shipping-queue'] })
      queryClient.invalidateQueries({ queryKey: ['shipping-queue-ids'] })
      // Navegar a la cola de envío
      onNavigateToQueue?.()
    },
    onError: (error: any) => {
      toast.error('Error al añadir a cola', error.response?.data?.detail || error.message)
    },
  })

  // Mutation para eliminar pedidos manuales
  const deleteManualOrdersMutation = useMutation({
    mutationFn: async (orderIds: number[]) => {
      // Eliminar cada pedido manual uno a uno
      const results = await Promise.allSettled(
        orderIds.map(id => shipping.deleteManualOrder(id))
      )
      const success = results.filter(r => r.status === 'fulfilled').length
      const failed = results.filter(r => r.status === 'rejected').length
      return { success, failed }
    },
    onSuccess: ({ success, failed }) => {
      if (success > 0) {
        toast.success(`${success} pedido(s) manual(es) eliminado(s)`)
      }
      if (failed > 0) {
        toast.error(`${failed} pedido(s) no se pudieron eliminar`)
      }
      setSelectedOrders([])
      queryClient.invalidateQueries({ queryKey: ['manual-orders-pending'] })
    },
    onError: (error: any) => {
      toast.error('Error al eliminar pedidos', error.response?.data?.detail || error.message)
    },
  })

  // Handlers
  const handleSelectAll = () => {
    // Usamos los pedidos pendientes ya filtrados, excluyendo los agrupados
    const selectableOrders = pendingOrders.filter(o => !o.is_grouped)
    const allOrderIds = selectableOrders.map(o => o.id)

    if (selectedOrders.length === allOrderIds.length) {
      setSelectedOrders([])
    } else {
      setSelectedOrders(allOrderIds)
    }
  }

  const handleSelectOrder = (orderId: number) => {
    // No permitir seleccionar pedidos agrupados
    const order = pendingOrders.find(o => o.id === orderId)
    if (order?.is_grouped) return

    setSelectedOrders(prev =>
      prev.includes(orderId)
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    )
  }

  const handleRowClick = (orderId: number) => {
    setSelectedOrderId(orderId)
  }

  // IDs de pedidos ya en cola (separados por tipo)
  const queueOrderIds = new Set(queueIdsQuery.data?.order_ids || [])
  const queueManualOrderIds = new Set(queueIdsQuery.data?.manual_order_ids || [])

  // Pedidos base de PrestaShop
  const psOrders = ordersQuery.data?.orders || []

  // Pedidos manuales
  const manualOrders = manualOrdersQuery.data?.orders || []

  // Combinar pedidos en formato unificado
  const unifiedOrders = useMemo((): UnifiedOrder[] => {
    // Convertir pedidos de PrestaShop
    const psUnified: UnifiedOrder[] = psOrders.map(o => ({
      id: o.id,
      reference: o.reference,
      date_add: o.date_add,
      customer_name: o.customer_name,
      customer_email: o.customer_email,
      shipping_address: o.shipping_address,
      total: o.total,
      items_count: o.items_count,
      carrier_name: o.carrier_name,
      payment_method: o.payment_method,
      dni: o.dni,
      vat_number: o.vat_number,
      has_tax: (o.total_paid_tax_incl ?? 0) > (o.total_paid_tax_excl ?? 0),
      source: 'prestashop' as const,
      original: o,
      is_grouped: o.is_grouped,
      grouped_with: o.grouped_with,
      service_point: o.service_point
    }))

    // Convertir pedidos manuales
    const manualUnified: UnifiedOrder[] = manualOrders.map(o => ({
      id: o.id,
      reference: o.custom_reference || o.reference,
      date_add: o.created_at || new Date().toISOString(),
      customer_name: o.recipient_name,
      customer_email: o.recipient_email || '',
      shipping_address: {
        country_code: normalizeCountryCode(o.address_country),
        postal_code: o.address_postal_code,
        street: o.address_street,
        city: o.address_city,
        phone: o.recipient_phone,
        phone_mobile: o.recipient_phone
      },
      total: o.total_value,
      items_count: o.products?.length || 0,
      carrier_name: o.carrier_code || '',
      payment_method: o.payment_method || '',
      dni: o.recipient_nif,
      has_tax: true,  // Pedidos manuales: asumimos que tienen IVA
      source: 'manual' as const,
      original: o
    }))

    // Combinar y ordenar por fecha (más recientes primero)
    return [...psUnified, ...manualUnified].sort((a, b) =>
      new Date(b.date_add).getTime() - new Date(a.date_add).getTime()
    )
  }, [psOrders, manualOrders])

  // Estado de facturación (semáforo pre-factura) de los pedidos de PrestaShop de la lista.
  // Consulta aparte: si falla o tarda, la bandera es solo la de envío y la lista funciona igual.
  const psIds = useMemo(
    () => unifiedOrders.filter(o => o.source === 'prestashop').map(o => o.id).sort((a, b) => a - b),
    [unifiedOrders]
  )
  const invoiceQuery = useQuery({
    queryKey: ['prefactura-status', psIds.join(',')],
    queryFn: () => prefactura.status(psIds),
    enabled: canInvoice && psIds.length > 0,
    staleTime: 60_000,
    // Al entrar o salir un pedido de la lista cambia la clave: se mantiene el estado anterior
    // mientras llega el nuevo (si no, la bandera y el botón «Leer comentarios» desaparecen un rato)
    placeholderData: keepPreviousData,
    // Sin esto los pedidos nuevos y los comentarios recién sincronizados no salen hasta pulsar «Actualizar»
    refetchInterval: 120_000,
    retry: 1,
    refetchOnWindowFocus: false,
  })
  const invoiceById = invoiceQuery.data?.orders || {}
  const readyToInvoiceIds = invoiceQuery.data?.ready_ids || []

  const refreshAll = async () => {
    ordersQuery.refetch()
    if (canInvoice && psIds.length > 0) {
      try {
        const fresh = await prefactura.status(psIds, true)
        queryClient.setQueryData(['prefactura-status', psIds.join(',')], fresh)
      } catch {
        // la parte de factura no está disponible: la bandera sigue con la de envío
      }
    }
  }

  const copyReadyToInvoice = async () => {
    if (readyToInvoiceIds.length === 0) {
      toast.warning('No hay pedidos listos para facturar')
      return
    }
    const text = readyToInvoiceIds.join(', ')
    try {
      await navigator.clipboard.writeText(text)
      toast.success(`${readyToInvoiceIds.length} pedidos listos copiados`)
    } catch {
      window.prompt('Copia la lista de pedidos listos para facturar:', text)
    }
  }

  // Opciones únicas para filtros
  const filterOptions = useMemo(() => {
    const carriers = new Set<string>()
    const payments = new Set<string>()
    const countries = new Set<string>()

    unifiedOrders.forEach(o => {
      carriers.add(getCarrierKey(o.carrier_name))
      payments.add(getPaymentKey(o.payment_method))
      countries.add(o.shipping_address.country_code)
    })

    return {
      carriers: Array.from(carriers).sort(),
      payments: Array.from(payments).sort(),
      countries: Array.from(countries).sort(),
      statuses: ['green', 'yellow', 'red'] as ValidationStatus[]
    }
  }, [unifiedOrders])

  // Helper para verificar VIES (intracomunitario)
  // Un pedido es intracomunitario SOLO si PrestaShop NO le cobró IVA
  // Esto es más fiable que verificar el formato del VAT, ya que PrestaShop
  // valida contra VIES antes de aplicar la exención de IVA
  const isViesOrder = (order: UnifiedOrder): boolean => {
    // Si el pedido tiene IVA cobrado, NO es intracomunitario
    // (independientemente del VAT que tenga el cliente)
    if (order.has_tax) return false

    // Si no tiene IVA, verificamos que tenga un VAT con formato europeo
    // para confirmar que es una operación intracomunitaria
    const VAT_PREFIXES = ['AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'EL', 'ES', 'FI', 'FR', 'GR', 'HR', 'HU', 'IE', 'IT', 'LT', 'LU', 'LV', 'MT', 'NL', 'PL', 'PT', 'RO', 'SE', 'SI', 'SK']
    const vatNumber = order.vat_number?.toUpperCase().replace(/[^A-Z0-9]/g, '') || ''

    if (!vatNumber) return false

    const hasEUVatFormat = VAT_PREFIXES.some(prefix => vatNumber.startsWith(prefix))
    return hasEUVatFormat && vatNumber.length >= 8
  }

  // Helper para validar pedido unificado (solo para PrestaShop, manuales siempre son green)
  const validateUnifiedOrder = (order: UnifiedOrder): ValidationResult => {
    if (order.source === 'manual') {
      return { status: 'green', issues: [] }
    }
    const v = validateOrder(order.original as ShippingOrder)
    if (!canInvoice) return v
    // Filtro OK/Aviso/Error sobre el color combinado envío + factura
    return { status: combineStatus(v, invoiceById[String(order.id)]), issues: v.issues }
  }

  // Filtrar pedidos
  const filteredOrders = useMemo(() => {
    let result = unifiedOrders

    // Búsqueda por texto
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      result = result.filter(o =>
        o.reference.toLowerCase().includes(query) ||
        o.customer_name.toLowerCase().includes(query) ||
        o.id.toString().includes(query)
      )
    }

    // Filtro por transportista
    if (filters.carrier.length > 0) {
      result = result.filter(o => filters.carrier.includes(getCarrierKey(o.carrier_name)))
    }

    // Filtro por forma de pago
    if (filters.payment.length > 0) {
      result = result.filter(o => filters.payment.includes(getPaymentKey(o.payment_method)))
    }

    // Filtro por país
    if (filters.country.length > 0) {
      result = result.filter(o => filters.country.includes(o.shipping_address.country_code))
    }

    // Filtro por estado
    if (filters.status.length > 0) {
      result = result.filter(o => filters.status.includes(validateUnifiedOrder(o).status))
    }

    // Filtro solo VIES
    if (filters.viesOnly) {
      result = result.filter(o => isViesOrder(o))
    }

    // Ocultar pedidos sin stock (rosas). Si se busca un pedido concreto, se muestra igualmente.
    if (hideNoStock && !searchQuery) {
      result = result.filter(o => !isNoStockOrder(o))
    }

    return result
  }, [unifiedOrders, searchQuery, filters, canInvoice, invoiceQuery.data, hideNoStock])

  // Pedidos rosas que la casilla está ocultando ahora mismo (sin envío activo ni en cola)
  // Pedidos de la lista con comentario del cliente u observaciones de su ficha sin leer y SIN otros
  // motivos en la bandera (los que tienen otros avisos se revisan desde el detalle de la bandera)
  const unreadMsgOrders = useMemo(
    () => (canInvoice ? filteredOrders.filter(o => {
      if (o.source !== 'prestashop') return false
      const inv = invoiceById[String(o.id)]
      return unreadMessages(inv).length > 0 && otherReasons(inv?.issues || [], validateOrder(o.original as ShippingOrder)).length === 0
    }) : []),
    [filteredOrders, invoiceById, canInvoice]
  )
  const openReader = () => setReaderItems(unreadMsgOrders.map(o => ({
    id: o.id, reference: o.reference, customer_name: o.customer_name, total: o.total, inv: invoiceById[String(o.id)],
  })))

  const hiddenNoStockCount = hideNoStock && !searchQuery
    ? unifiedOrders.filter(o => {
        if (!isNoStockOrder(o)) return false
        const psOrder = o.original as ShippingOrder
        const hasActiveShipment = psOrder.shipment?.tracking_number && psOrder.shipment?.status !== 'cancelled'
        return !hasActiveShipment && !queueOrderIds.has(o.id)
      }).length
    : 0

  // Solo mostrar pedidos sin envío activo Y que no estén ya en cola
  // (Los pedidos con envío CANCELADO sí aparecen para poder re-procesarlos)
  const pendingOrders = filteredOrders.filter(o => {
    // Para pedidos de PrestaShop, verificar que no tengan envío activo ni estén en cola
    if (o.source === 'prestashop') {
      const psOrder = o.original as ShippingOrder
      // Tiene envío activo si tiene tracking Y el estado NO es cancelled
      const hasActiveShipment = psOrder.shipment?.tracking_number &&
        psOrder.shipment?.status !== 'cancelled'
      return !hasActiveShipment && !queueOrderIds.has(o.id)
    }
    // Para pedidos manuales, verificar status pending Y que no estén en cola
    const manualOrder = o.original as ManualOrder
    return manualOrder.status === 'pending' && !queueManualOrderIds.has(o.id)
  })

  // Verificar si hay filtros activos
  const hasActiveFilters = filters.carrier.length > 0 || filters.payment.length > 0 ||
    filters.country.length > 0 || filters.status.length > 0 || filters.viesOnly

  const clearAllFilters = () => setFilters(emptyFilters)

  // Handler para eliminar pedidos seleccionados
  const handleDeleteOrders = () => {
    // Separar pedidos por tipo
    const manualOrderIds: number[] = []
    const psOrderIds: number[] = []

    selectedOrders.forEach(id => {
      const order = pendingOrders.find(o => o.id === id)
      if (order?.source === 'manual') {
        manualOrderIds.push(id)
      } else if (order?.source === 'prestashop') {
        psOrderIds.push(id)
      }
    })

    // Mostrar advertencia para pedidos de PrestaShop
    if (psOrderIds.length > 0) {
      toast.warning(`${psOrderIds.length} pedido(s) de PrestaShop no se pueden eliminar desde aquí`)
    }

    // Eliminar pedidos manuales
    if (manualOrderIds.length > 0) {
      deleteManualOrdersMutation.mutate(manualOrderIds)
    } else if (psOrderIds.length > 0) {
      // Solo había pedidos de PrestaShop, limpiar selección
      setSelectedOrders([])
    }
  }

  // Buscar pedido seleccionado en ambas fuentes
  const selectedUnifiedOrder = unifiedOrders.find(o => o.id === selectedOrderId)
  const selectedPsOrder = selectedUnifiedOrder?.source === 'prestashop'
    ? selectedUnifiedOrder.original as ShippingOrder
    : null
  const selectedManualOrder = selectedUnifiedOrder?.source === 'manual'
    ? selectedUnifiedOrder.original as ManualOrder
    : null

  return (
    <div className="flex h-full">
      {/* Contenido principal */}
      <div className="flex-1 flex flex-col">
        {/* Toolbar */}
        <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-3">
          <div className="flex items-center justify-between">
            {/* Búsqueda */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Buscar por número de pedido..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 w-80 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>

            <label
              className="flex items-center gap-2 ml-4 mr-auto text-sm text-slate-600 dark:text-slate-300 cursor-pointer select-none"
              title="Oculta los pedidos rosas (pendiente por falta de stock). Buscando su número se muestran igualmente."
            >
              <input
                type="checkbox"
                checked={hideNoStock}
                onChange={(e) => toggleHideNoStock(e.target.checked)}
                className="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-pink-600 focus:ring-pink-500"
              />
              Ocultar pedidos sin stock
              {hiddenNoStockCount > 0 && (
                <span className="px-1.5 py-0.5 rounded bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300 text-xs">
                  {hiddenNoStockCount} ocultos
                </span>
              )}
            </label>

            {/* Acciones */}
            <div className="flex items-center gap-3">
              {/* Botón crear envío manual */}
              <button
                onClick={() => setShowManualOrderModal(true)}
                className="flex items-center gap-2 px-3 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors"
                title="Crear envío manual"
              >
                <Plus className="w-4 h-4" />
                <span className="text-sm font-medium">Envío manual</span>
              </button>
              {canInvoice && unreadMsgOrders.length > 0 && (
                <button
                  onClick={openReader}
                  className="flex items-center gap-2 px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg shadow-sm transition-colors"
                  title="Lee seguidos los comentarios y observaciones de cliente de los pedidos de la lista que no tienen otros avisos, y decide si quedan en verde o ámbar"
                >
                  <MessageSquareText className="w-4 h-4" />
                  <span className="text-sm font-medium">Leer comentarios ({unreadMsgOrders.length})</span>
                </button>
              )}
              {canInvoice && (
                <button
                  onClick={copyReadyToInvoice}
                  disabled={!invoiceQuery.data}
                  className="flex items-center gap-2 px-3 py-2 border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 rounded-lg transition-colors"
                  title="Copia los nº de pedido listos para facturar (para marcarlos en 'Generación de facturas' de Factusol), también los que ya no salen aquí porque se generó la etiqueta"
                >
                  <ClipboardCopy className="w-4 h-4" />
                  <span className="text-sm font-medium">
                    Copiar listos para facturar{invoiceQuery.data ? ` (${readyToInvoiceIds.length})` : ''}
                  </span>
                </button>
              )}
              <div className="w-px h-6 bg-slate-200 dark:bg-slate-600" />
              <button
                onClick={refreshAll}
                disabled={ordersQuery.isFetching}
                className="p-2 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
                title="Actualizar"
              >
                <RefreshCw className={`w-5 h-5 ${ordersQuery.isFetching ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Tabla */}
        <div className="flex-1 overflow-auto">
          <div className="bg-white dark:bg-slate-800 m-6 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
            {/* Barra de filtros activos */}
            {hasActiveFilters && (
              <div className="flex items-center gap-2 px-5 py-2 bg-teal-50 dark:bg-teal-900/20 border-b border-teal-100 dark:border-teal-900/50">
                <span className="text-xs text-teal-700 dark:text-teal-300">Filtros activos:</span>
                <div className="flex items-center gap-1 flex-wrap">
                  {filters.carrier.map(c => (
                    <span key={c} className="inline-flex items-center gap-1 px-2 py-0.5 bg-teal-100 dark:bg-teal-800 text-teal-700 dark:text-teal-300 rounded text-xs">
                      {c}
                      <button onClick={() => setFilters(f => ({ ...f, carrier: f.carrier.filter(x => x !== c) }))} className="hover:text-teal-900">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                  {filters.payment.map(p => (
                    <span key={p} className="inline-flex items-center gap-1 px-2 py-0.5 bg-teal-100 dark:bg-teal-800 text-teal-700 dark:text-teal-300 rounded text-xs">
                      {p}
                      <button onClick={() => setFilters(f => ({ ...f, payment: f.payment.filter(x => x !== p) }))} className="hover:text-teal-900">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                  {filters.country.map(c => (
                    <span key={c} className="inline-flex items-center gap-1 px-2 py-0.5 bg-teal-100 dark:bg-teal-800 text-teal-700 dark:text-teal-300 rounded text-xs">
                      {c}
                      <button onClick={() => setFilters(f => ({ ...f, country: f.country.filter(x => x !== c) }))} className="hover:text-teal-900">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                  {filters.status.map(s => (
                    <span key={s} className="inline-flex items-center gap-1 px-2 py-0.5 bg-teal-100 dark:bg-teal-800 text-teal-700 dark:text-teal-300 rounded text-xs">
                      {s === 'green' ? '✓ OK' : s === 'yellow' ? '⚠ Aviso' : '✗ Error'}
                      <button onClick={() => setFilters(f => ({ ...f, status: f.status.filter(x => x !== s) }))} className="hover:text-teal-900">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                  {filters.viesOnly && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-teal-100 dark:bg-teal-800 text-teal-700 dark:text-teal-300 rounded text-xs">
                      Solo VIES
                      <button onClick={() => setFilters(f => ({ ...f, viesOnly: false }))} className="hover:text-teal-900">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                </div>
                <button
                  onClick={clearAllFilters}
                  className="ml-auto text-xs text-teal-600 dark:text-teal-400 hover:text-teal-800 dark:hover:text-teal-200"
                >
                  Limpiar todos
                </button>
              </div>
            )}

            {/* Header de tabla */}
            <div className="grid grid-cols-[40px_50px_105px_150px_70px_75px_110px_90px_50px_50px] gap-8 px-5 py-3 bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700 text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              <div className="flex items-center justify-center">
                <input
                  type="checkbox"
                  checked={selectedOrders.length > 0 && selectedOrders.length === pendingOrders.filter(o => !o.is_grouped).length}
                  onChange={handleSelectAll}
                  className="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-teal-600 focus:ring-teal-500"
                />
              </div>
              <div></div>
              <div className="flex items-center gap-1">
                Fecha
                <ChevronDown className="w-3 h-3" />
              </div>
              <div>Pedido</div>
              <div className="flex items-center justify-center gap-1">
                <span>Transp.</span>
                <FilterDropdown
                  label="Transportista"
                  options={filterOptions.carriers}
                  selected={filters.carrier}
                  onChange={(values) => setFilters(f => ({ ...f, carrier: values }))}
                />
              </div>
              <div className="flex items-center justify-center gap-1">
                <span>Pago</span>
                <FilterDropdown
                  label="Forma de pago"
                  options={filterOptions.payments}
                  selected={filters.payment}
                  onChange={(values) => setFilters(f => ({ ...f, payment: values }))}
                />
              </div>
              <div className="flex items-center gap-1">
                <span>Destino</span>
                <FilterDropdown
                  label="País"
                  options={filterOptions.countries}
                  selected={filters.country}
                  onChange={(values) => setFilters(f => ({ ...f, country: values }))}
                />
                <button
                  onClick={() => setFilters(f => ({ ...f, viesOnly: !f.viesOnly }))}
                  className={`px-1.5 py-0.5 text-[10px] font-bold rounded transition-colors ${
                    filters.viesOnly
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-200 text-slate-500 dark:bg-slate-600 dark:text-slate-400 hover:bg-slate-300 dark:hover:bg-slate-500'
                  }`}
                  title="Filtrar solo pedidos VIES"
                >
                  VIES
                </button>
              </div>
              <div className="text-right">Total</div>
              <div className="text-center">Uds</div>
              <div className="flex items-center justify-center gap-1">
                <span>Estado</span>
                <FilterDropdown
                  label="Estado"
                  options={filterOptions.statuses}
                  selected={filters.status}
                  onChange={(values) => setFilters(f => ({ ...f, status: values as ValidationStatus[] }))}
                  renderOption={(value) => (
                    <span className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${
                        value === 'green' ? 'bg-green-500' : value === 'yellow' ? 'bg-amber-500' : 'bg-red-500'
                      }`} />
                      {value === 'green' ? 'OK' : value === 'yellow' ? 'Aviso' : 'Error'}
                    </span>
                  )}
                />
              </div>
            </div>

            {/* Contenido */}
            {ordersQuery.isLoading ? (
              <div className="flex flex-col items-center justify-center py-16">
                <Loader2 className="w-8 h-8 text-teal-600 animate-spin mb-3" />
                <p className="text-slate-500 dark:text-slate-400">Cargando pedidos...</p>
              </div>
            ) : ordersQuery.isError ? (
              <div className="flex flex-col items-center justify-center py-16">
                <XCircle className="w-8 h-8 text-red-500 mb-3" />
                <p className="text-red-600 dark:text-red-400">Error cargando pedidos</p>
              </div>
            ) : pendingOrders.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16">
                <Package className="w-12 h-12 text-slate-300 dark:text-slate-600 mb-3" />
                <p className="text-slate-500 dark:text-slate-400">No hay pedidos pendientes</p>
                <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">
                  Los pedidos nuevos de PrestaShop aparecerán aquí
                </p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100 dark:divide-slate-700/50">
                {pendingOrders.map((order) => (
                  <div
                    key={order.id}
                    className={`grid grid-cols-[40px_50px_105px_150px_70px_75px_110px_90px_50px_50px] gap-8 px-5 py-3 items-center cursor-pointer transition-colors ${
                      order.is_grouped
                        ? 'bg-blue-50/50 dark:bg-blue-900/10 hover:bg-blue-100/50 dark:hover:bg-blue-900/20'
                        : selectedOrders.includes(order.id)
                          ? 'bg-teal-50 dark:bg-teal-900/20 hover:bg-teal-100 dark:hover:bg-teal-900/30'
                          : 'hover:bg-slate-50 dark:hover:bg-slate-900/30'
                    }`}
                    onClick={() => handleRowClick(order.id)}
                  >
                    {/* Checkbox */}
                    <div className="flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedOrders.includes(order.id)}
                        onChange={() => handleSelectOrder(order.id)}
                        disabled={order.is_grouped}
                        title={order.is_grouped ? `Este pedido va agrupado con ${order.grouped_with?.parent_order_reference}` : undefined}
                        className={`w-4 h-4 rounded border-slate-300 dark:border-slate-600 focus:ring-teal-500 ${
                          order.is_grouped
                            ? 'text-slate-300 dark:text-slate-600 cursor-not-allowed'
                            : 'text-teal-600'
                        }`}
                      />
                    </div>

                    {/* Fuente */}
                    <div className="flex items-center justify-center">
                      {order.source === 'prestashop' ? (
                        <PrestaShopLogo className="w-6 h-6" />
                      ) : (
                        <ManualOrderLogo className="w-6 h-6" />
                      )}
                    </div>

                    {/* Fecha */}
                    <div className="text-xs text-slate-600 dark:text-slate-400">
                      <div className="font-medium">{new Date(order.date_add).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })}</div>
                      <div className="text-slate-400">{new Date(order.date_add).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}</div>
                    </div>

                    {/* Pedido */}
                    <div>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-sm font-semibold text-slate-900 dark:text-white">{order.reference}</span>
                        {/* Indicador de pedido agrupado */}
                        {order.is_grouped && order.grouped_with && (
                          <span
                            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 rounded"
                            title={`Este pedido va en el mismo envío que ${order.grouped_with.parent_order_reference}`}
                          >
                            <Link2 className="w-2.5 h-2.5" />
                            {order.grouped_with.parent_order_reference}
                          </span>
                        )}
                        {/* Compra dividida: otros pedidos de la misma compra (cada uno con su envío) */}
                        {order.source === 'prestashop' && ((order.original as ShippingOrder).split_siblings?.length ?? 0) > 0 && (
                          <span
                            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400 rounded"
                            title={`Compra dividida por el cliente: cada pedido es independiente y sale con su propio envío. Otros pedidos de la compra: ${(order.original as ShippingOrder).split_siblings!.map(s => `#${s.id} (${s.kind === 'backorder' ? 'bajo pedido' : 'en stock'})`).join(', ')}`}
                          >
                            <Link2 className="w-2.5 h-2.5" />
                            Dividido · {(order.original as ShippingOrder).split_siblings!.map(s => `#${s.id}`).join(' ')}
                          </span>
                        )}
                        {/* Indicador de envío cancelado */}
                        {order.source === 'prestashop' && (order.original as ShippingOrder).shipment?.status === 'cancelled' && (
                          <span
                            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 rounded"
                            title="Este pedido tuvo un envío que fue cancelado"
                          >
                            <RotateCcw className="w-2.5 h-2.5" />
                            Reenvío
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-400">#{order.id}</div>
                    </div>

                    {/* Transportista */}
                    <div className="flex items-center justify-center" title={order.carrier_name}>
                      <CarrierBadge carrierName={order.carrier_name} />
                    </div>

                    {/* Forma de pago */}
                    <div className="flex items-center justify-center" title={order.payment_method}>
                      <PaymentBadge paymentMethod={order.payment_method} />
                    </div>

                    {/* Destino + VIES combinado */}
                    <div className="flex items-center gap-2">
                      <CountryFlag countryCode={order.shipping_address.country_code} />
                      <span className="text-sm text-slate-700 dark:text-slate-300">{order.shipping_address.country_code}</span>
                      <ViesBadge dni={order.dni} vatNumber={order.vat_number} countryCode={order.shipping_address.country_code} />
                      {order.service_point && (
                        <span
                          title={`📍 ${order.service_point.carrier === 'fedex' ? 'Punto FedEx: ' : ''}${order.service_point.name}\n${order.service_point.address}, ${order.service_point.postal_code} ${order.service_point.city}`}
                          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300 cursor-help"
                        >
                          <MapPin className="w-3 h-3" />SP
                        </span>
                      )}
                    </div>

                    {/* Total */}
                    <div className="text-sm font-semibold text-slate-900 dark:text-white text-right">
                      {order.total.toFixed(2)} €
                    </div>

                    {/* Productos */}
                    <div className="text-sm text-slate-500 text-center">
                      {order.items_count}
                    </div>

                    {/* Estado */}
                    <div className="flex items-center justify-center">
                      {order.source === 'prestashop' ? (
                        canInvoice ? (
                          <CombinedFlag
                            shipping={validateOrder(order.original as ShippingOrder)}
                            invoice={invoiceById[String(order.id)]}
                            invoiceLoading={invoiceQuery.isLoading}
                            invoiceFailed={invoiceQuery.isError}
                            onOpenInvoice={() => setInvoiceModalId(order.id)}
                          />
                        ) : (
                          <StatusIndicator order={order.original as ShippingOrder} />
                        )
                      ) : (
                        // Pedidos manuales siempre en verde (ya están validados al crearlos)
                        <FlagTooltip
                          className="relative flex items-center justify-center"
                          content={
                            <div className="bg-slate-900 text-white text-xs rounded-lg px-2 py-1 shadow-lg whitespace-nowrap">
                              Envío manual - listo
                            </div>
                          }
                        >
                          <div className="w-4 h-5 bg-green-500 rounded-sm relative">
                            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[8px] border-r-[8px] border-b-[4px] border-l-transparent border-r-transparent border-b-white dark:border-b-slate-800" />
                          </div>
                        </FlagTooltip>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Panel lateral de acciones */}
      {selectedOrders.length > 0 && (
        <div className="w-72 bg-white dark:bg-slate-800 border-l border-slate-200 dark:border-slate-700 p-5 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-teal-100 dark:bg-teal-900/50 flex items-center justify-center">
              <span className="text-teal-600 dark:text-teal-400 font-bold">{selectedOrders.length}</span>
            </div>
            <span className="text-sm text-slate-600 dark:text-slate-400">
              pedido{selectedOrders.length > 1 ? 's' : ''} seleccionado{selectedOrders.length > 1 ? 's' : ''}
            </span>
          </div>

          <button
            onClick={() => addToQueueMutation.mutate(selectedOrders)}
            disabled={addToQueueMutation.isPending}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 disabled:opacity-50 transition-colors"
          >
            {addToQueueMutation.isPending ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Package className="w-5 h-5" />
            )}
            Añadir a cola de empaquetado
          </button>

          <button
            onClick={handleDeleteOrders}
            disabled={deleteManualOrdersMutation.isPending}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-lg font-medium hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 transition-colors"
          >
            {deleteManualOrdersMutation.isPending ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Trash2 className="w-5 h-5" />
            )}
            Eliminar
          </button>
        </div>
      )}

      {/* Modal de detalle de pedido (solo para PrestaShop por ahora) */}
      {selectedPsOrder && (
        <OrderDetailModal
          key={selectedPsOrder.id}
          order={selectedPsOrder}
          onClose={() => setSelectedOrderId(null)}
          onSave={async (data) => {
            await shipping.updateOrder(selectedPsOrder.id, {
              name: data.name,
              company: data.company,
              email: data.email,
              phone: data.phone,
              street: data.street,
              street2: data.street2,
              city: data.city,
              postal_code: data.postalCode,
              country_code: data.country,
              dni: data.nif,
            })
            toast.success('Pedido actualizado correctamente')
            queryClient.invalidateQueries({ queryKey: ['shipping-orders-pending'] })
            setSelectedOrderId(null)
          }}
          onAddToQueue={async (orderId) => {
            await shipping.addToQueue({ order_ids: [orderId] })
            toast.success('Pedido añadido a la cola')
            queryClient.invalidateQueries({ queryKey: ['shipping-orders-pending'] })
            queryClient.invalidateQueries({ queryKey: ['shipping-queue'] })
            queryClient.invalidateQueries({ queryKey: ['shipping-queue-ids'] })
            setSelectedOrderId(null)
            onNavigateToQueue?.()
          }}
        />
      )}

      {/* Modal de facturación (semáforo pre-factura) */}
      {invoiceModalId !== null && (
        <InvoiceCheckModal
          orderId={invoiceModalId}
          reference={unifiedOrders.find(o => o.id === invoiceModalId)?.reference}
          shipping={(() => {
            const o = unifiedOrders.find(x => x.id === invoiceModalId)
            return o && o.source === 'prestashop' ? validateOrder(o.original as ShippingOrder) : undefined
          })()}
          onClose={() => setInvoiceModalId(null)}
        />
      )}

      {/* Lector rápido de comentarios y observaciones del cliente */}
      {readerItems && readerItems.length > 0 && (
        <MessagesReaderModal items={readerItems} onClose={() => setReaderItems(null)} />
      )}

      {/* Modal para crear envío manual */}
      {showManualOrderModal && (
        <ManualOrderModal
          onClose={() => setShowManualOrderModal(false)}
          onSuccess={() => {
            setShowManualOrderModal(false)
            toast.success('Envío manual creado correctamente')
            queryClient.invalidateQueries({ queryKey: ['manual-orders-pending'] })
          }}
        />
      )}

      {/* Modal para editar pedido manual */}
      {selectedManualOrder && (
        <ManualOrderModal
          key={selectedManualOrder.id}
          editingOrder={selectedManualOrder}
          onClose={() => setSelectedOrderId(null)}
          onSuccess={() => {
            setSelectedOrderId(null)
            toast.success('Envío manual actualizado correctamente')
            queryClient.invalidateQueries({ queryKey: ['manual-orders-pending'] })
          }}
        />
      )}
    </div>
  )
}
